import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaSpinner, FaExclamationCircle } from "react-icons/fa";
import PostCard from "../../components/profile/PostCard";
import api from "../../utils/api/axiosInstance";
import styles from "../../styles/pages/post/PostDetailsPage.module.css";
import PageSeo from "../../components/common/PageSeo";

const PostDetailsPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPost = useCallback(async (signal) => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/posts/${postId}`, { signal });
      if (signal?.aborted) return;
      setPost(res.data.post || res.data);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      console.error("Failed to load post:", err);
      setError(
        err.response?.data?.message ||
          "We couldn't load this post. It may have been deleted."
      );
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    if (!postId) return undefined;
    const controller = new AbortController();
    fetchPost(controller.signal);
    return () => controller.abort();
  }, [postId, fetchPost]);

  useEffect(() => {
    const shouldScroll =
      location.state?.scrollToComments || location.hash === "#comments-section";
    if (!shouldScroll || !post) return undefined;

    let cancelled = false;
    let timerId;

    const scrollToComments = (attempt = 0) => {
      if (cancelled) return;
      const commentsSection = document.getElementById("comments-section");
      if (commentsSection) {
        commentsSection.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        return;
      }
      if (attempt < 8) {
        timerId = window.setTimeout(() => scrollToComments(attempt + 1), 100);
      }
    };

    timerId = window.setTimeout(() => scrollToComments(0), 50);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [post, location.hash, location.state]);

  const handlePostDeleted = () => {
    // If post is deleted from this page, go back or home
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const handlePostUpdated = (updatedPost) => {
    setPost(updatedPost);
  };

  return (
    <div className="pageContainer">
      <PageSeo
        title={
          post?.user?.firstName
            ? `Post by ${post.user.firstName} ${post.user.lastName || ""}`.trim()
            : "Post"
        }
        pathname={`/post/${postId}`}
        noIndex
      />
      <div className="innerContainer">
        <div className={styles.postDetailsPage}>
          {loading ? (
            <div className={styles.stateContainer}>
              <div className={styles.loadingState}>
                <FaSpinner className={styles.spinner} />
                <p>Loading post...</p>
              </div>
            </div>
          ) : error ? (
            <div className={styles.stateContainer}>
              <div className={styles.errorState}>
                <FaExclamationCircle />
                <h3>Unable to Load Post</h3>
                <p>{error}</p>
                <button
                  className={styles.retryButton}
                  onClick={() => fetchPost()}
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : !post ? (
            <div className={styles.stateContainer}>
              <div className={styles.errorState}>
                <FaExclamationCircle />
                <h3>Post Not Found</h3>
                <p>This post may have been deleted or doesn't exist.</p>
                <button
                  className={styles.retryButton}
                  onClick={() => navigate("/")}
                >
                  Home
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.postWrapper}>
              <PostCard
                post={post}
                onDelete={handlePostDeleted}
                onPostUpdated={handlePostUpdated}
                isGridView={false}
                defaultShowComments
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostDetailsPage;
